'use client';

import { Children, type ReactNode } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { Typography, type TypographyProps } from '../typography/Typography';

type Variant = VariantProps<typeof indicatorStyles>;

const indicatorClasses = {
  root: 'zds-indicator-root',
  bubble: 'zds-indicator-bubble',
  label: 'zds-indicator-label',
} as const;

const indicatorStyles = tv({
  slots: {
    root: [indicatorClasses.root, 'inline-flex'],
    bubble: [
      indicatorClasses.bubble,
      'inline-flex',
      'items-center',
      'justify-center',
      'rounded-full',
      'border-1',
    ],
    label: [indicatorClasses.label, 'leading-none'],
  },
  variants: {
    variant: {
      number: {
        bubble: [
          'z--indicator-number-min-width',
          'z--indicator-number-height',
          'px-[6px]',
        ],
      },
      dot: {
        bubble: ['z--indicator-dot-width', 'z--indicator-dot-height'],
      },
    },
    color: {
      'static-white': {
        bubble: ['bg-static-white-main', 'text-static-black-main'],
      }, // this is needed for the SelectionChip color="forceWhite" elevated={false}
      'static-black': {
        bubble: ['bg-static-black-main', 'text-static-white-main'],
      }, // this is needed for the SelectionChip color="forceWhite" elevated={true}
      'accent-primary': {
        bubble: [
          'bg-accent-primary-main',
          'text-accent-primary-main-foreground',
        ],
      },
      'accent-secondary': {
        bubble: [
          'bg-accent-secondary-main',
          'text-accent-secondary-main-foreground',
        ],
      },
      success: { bubble: ['bg-success-main', 'text-success-main-foreground'] },
      error: { bubble: ['bg-error-main', 'text-error-main-foreground'] },
      warning: { bubble: ['bg-warning-main', 'text-warning-main-foreground'] },
      info: { bubble: ['bg-info-main', 'text-info-main-foreground'] },
      'brand-primary-a': {
        bubble: [
          'bg-brand-primary-a-main',
          'text-brand-primary-a-main-foreground',
        ],
      },
      'brand-primary-b': {
        bubble: [
          'bg-brand-primary-b-main',
          'text-brand-primary-b-main-foreground',
        ],
      },
      'brand-secondary-a': {
        bubble: [
          'bg-brand-secondary-a-main',
          'text-brand-secondary-a-main-foreground',
        ],
      },
      'brand-secondary-b': {
        bubble: [
          'bg-brand-secondary-b-main',
          'text-brand-secondary-b-main-foreground',
        ],
      },
      red: { bubble: ['bg-red-main', 'text-red-main-foreground'] },
      orange: { bubble: ['bg-orange-main', 'text-orange-main-foreground'] },
      yellow: { bubble: ['bg-yellow-main', 'text-yellow-main-foreground'] },
      lime: { bubble: ['bg-lime-main', 'text-lime-main-foreground'] },
      green: { bubble: ['bg-green-main', 'text-green-main-foreground'] },
      emerald: { bubble: ['bg-emerald-main', 'text-emerald-main-foreground'] },
      cyan: { bubble: ['bg-cyan-main', 'text-cyan-main-foreground'] },
      blue: { bubble: ['bg-blue-main', 'text-blue-main-foreground'] },
      sky: { bubble: ['bg-sky-main', 'text-sky-main-foreground'] },
      violet: { bubble: ['bg-violet-main', 'text-violet-main-foreground'] },
      fuchsia: { bubble: ['bg-fuchsia-main', 'text-fuchsia-main-foreground'] },
      pink: { bubble: ['bg-pink-main', 'text-pink-main-foreground'] },
      neutral: { bubble: ['bg-neutral-strong-1', 'text-neutral-muted-5'] },
    },
    hideOutline: {
      true: { bubble: ['border-transparent'] },
      false: { bubble: ['border-[var(--surface-neutral-layer-3)]'] },
    },
    visibility: {
      hidden: { bubble: ['invisible'] },
      visible: {},
    },
    isAnchored: {
      false: {},
      true: { root: ['relative'], bubble: ['absolute'] },
    },
    placement: {
      'top-right': {},
      'top-left': {},
      'bottom-right': {},
      'bottom-left': {},
    },
    overlap: {
      rectangular: {},
      circular: {},
    },
  },
  compoundVariants: [
    {
      color: 'static-white',
      hideOutline: false,
      class: { bubble: ['border-static-black'] },
    },
    {
      color: 'static-black',
      hideOutline: false,
      class: { bubble: ['border-static-white'] },
    },
    // isAnchored rectangular — translate centres the bubble on the corner point
    {
      isAnchored: true,
      placement: 'top-right',
      class: { bubble: ['top-0', 'right-0', '[translate:50%_-50%]'] },
    },
    {
      isAnchored: true,
      placement: 'top-left',
      class: { bubble: ['top-0', 'left-0', '[translate:-50%_-50%]'] },
    },
    {
      isAnchored: true,
      placement: 'bottom-right',
      class: { bubble: ['bottom-0', 'right-0', '[translate:50%_50%]'] },
    },
    {
      isAnchored: true,
      placement: 'bottom-left',
      class: { bubble: ['bottom-0', 'left-0', '[translate:-50%_50%]'] },
    },
    // circular overlap — 14% inset places anchor on the visual arc at 45°; translate unchanged
    {
      isAnchored: true,
      placement: 'top-right',
      overlap: 'circular',
      class: { bubble: ['top-[14%]', 'right-[14%]'] },
    },
    {
      isAnchored: true,
      placement: 'top-left',
      overlap: 'circular',
      class: { bubble: ['top-[14%]', 'left-[14%]'] },
    },
    {
      isAnchored: true,
      placement: 'bottom-right',
      overlap: 'circular',
      class: { bubble: ['bottom-[14%]', 'right-[14%]'] },
    },
    {
      isAnchored: true,
      placement: 'bottom-left',
      overlap: 'circular',
      class: { bubble: ['bottom-[14%]', 'left-[14%]'] },
    },
  ],
});

interface IndicatorSlotProps {
  /** Additional props for the bubble span. */
  bubble?: ComponentProps<object, 'span'>;
  /** Additional props for the label span (number variant only). */
  label?: Omit<TypographyProps<'span'>, 'component'>;
}

interface IndicatorBaseShared {
  /**
   * The host element the indicator anchors to. Omit to render standalone (inline pill).
   */
  children?: ReactNode;
  /**
   * Colour of the indicator.
   * @default 'accent-primary'
   */
  color?: Variant['color'];
  /**
   * Corner of the host at which to anchor the bubble. Ignored in standalone mode (no `children`).
   * @default 'top-right'
   */
  placement?: Variant['placement'];
  /**
   * Positions the bubble on the visual arc of a circular host. Ignored in standalone mode.
   * @default 'rectangular'
   */
  overlap?: Variant['overlap'];
  /**
   * Hide the 1 px border.
   * @default false
   */
  hideOutline?: boolean;
  /**
   * Hide the bubble while keeping the host and layout intact.
   */
  visibility?: Variant['visibility'];
  /**
   * Props applied to individual internal slots.
   */
  slotProps?: IndicatorSlotProps;
}

interface IndicatorNumberProps extends IndicatorBaseShared {
  variant: 'number';
  /**
   * The numeric value to display. `undefined` hides the indicator; `0` hides
   * unless `showZero` is `true`. Values above `max` render as `${max}+`.
   */
  value?: number;
  /**
   * Maximum value before truncation. Strict `>`, so `value === max` renders
   * untruncated.
   * @default 99
   */
  max?: number;
  /**
   * When `true`, render the literal `0` instead of hiding when `value === 0`.
   * @default false
   */
  showZero?: boolean;
}

interface IndicatorDotProps extends IndicatorBaseShared {
  variant: 'dot';
  value?: never;
  max?: never;
  showZero?: never;
}

type IndicatorBaseProps = IndicatorNumberProps | IndicatorDotProps;

type IndicatorProps = ComponentProps<IndicatorBaseProps, 'span'>;

type IndicatorComponent = Component<IndicatorBaseProps, 'span'>;

const formatCount = (value: number, max: number): string =>
  value > max ? `${max}+` : String(value);

const isHiddenComputed = (
  visibility: IndicatorProps['visibility'],
  isNumber: boolean,
  value: number | undefined,
  showZero: IndicatorProps['showZero']
): IndicatorProps['visibility'] => {
  if (
    visibility === 'hidden' ||
    (isNumber && (value === undefined || (value === 0 && !showZero)))
  ) {
    return 'hidden';
  } else {
    return 'visible';
  }
};

const Indicator: IndicatorComponent = (props) => {
  const {
    variant,
    children,
    className,
    color: colorProp,
    value: valueProp,
    max: maxProp,
    showZero: showZeroProp,
    placement = 'top-right',
    overlap = 'rectangular',
    hideOutline = false,
    visibility: visibilityProp,
    ref,
    slotProps,
    ...rest
  } = props;

  const isNumber = variant === 'number';
  const value = isNumber ? valueProp : undefined;
  const max = isNumber ? (maxProp ?? 99) : 99;
  const showZero = isNumber ? (showZeroProp ?? false) : false;

  const visibility = isHiddenComputed(
    visibilityProp,
    isNumber,
    value,
    showZero
  );
  const isAnchored = Children.count(children) > 0;

  if (
    !isAnchored &&
    isNumber &&
    (value === undefined || (value === 0 && !showZero))
  ) {
    return null;
  }

  const isLabelled =
    rest['aria-label'] !== undefined || rest['aria-labelledby'] !== undefined;

  const styles = indicatorStyles({
    variant,
    color: colorProp ?? 'accent-primary',
    hideOutline,
    visibility,
    isAnchored,
    placement,
    overlap,
  });

  const formattedCount =
    isNumber && typeof value === 'number' ? formatCount(value, max) : '';

  return (
    <span
      {...rest}
      ref={ref}
      role={isLabelled ? 'status' : undefined}
      className={styles.root({ className })}
    >
      {children}
      <span
        {...slotProps?.bubble}
        className={styles.bubble({ className: slotProps?.bubble?.className })}
      >
        {isNumber && (
          <Typography
            component="span"
            variant="caption"
            {...slotProps?.label}
            className={styles.label({ className: slotProps?.label?.className })}
          >
            {formattedCount}
          </Typography>
        )}
      </span>
    </span>
  );
};

Indicator.displayName = 'Indicator';

export {
  Indicator,
  type IndicatorProps,
  type IndicatorBaseProps,
  indicatorClasses,
};
